import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  AlertTriangle,
  ShoppingCart,
  Users,
  Target,
  Clock,
  Calendar,
  BarChart3,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Award,
  Zap,
  AlertCircle,
  Lightbulb,
  RotateCcw,
  Snail,
} from "lucide-react";
import { toast } from "sonner";
import { SalesPatternCard } from "@/components/SalesPatternCard";
import { ImportanceOneAlert } from "@/components/dashboard/ImportanceOneAlert";

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', { 
    style: 'currency', 
    currency: 'ZAR',
    minimumFractionDigits: 2 
  }).format(value || 0);
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function SmartDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [todaySales, setTodaySales] = useState({ total: 0, count: 0, target: 0 });
  const [topProducts, setTopProducts] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [salesByDay, setSalesByDay] = useState([]);
  const [salesByHour, setSalesByHour] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [weekComparison, setWeekComparison] = useState({ thisWeek: 0, lastWeek: 0, change: 0 });
  const [topCategories, setTopCategories] = useState([]);
  const [insights, setInsights] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [productsNeedingReview, setProductsNeedingReview] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDashboardSummary(),
        loadLowStock(),
        loadSalesByHour(),
        loadInsights(),
        loadProductsNeedingReview(),
      ]);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  // Main dashboard summary - combines live + imported data
  const loadDashboardSummary = async () => {
    try {
      const res = await axios.get(`${API}/api/dashboard/summary`);
      const data = res.data;
      
      // Update today's sales
      setTodaySales({
        total: data.today?.total || 0,
        count: data.today?.count || 0,
        target: data.today?.target || 10000
      });
      
      // Update week comparison
      setWeekComparison({
        thisWeek: data.week_comparison?.this_week || 0,
        lastWeek: data.week_comparison?.last_week || 0,
        change: data.week_comparison?.change || 0,
        thisWeekTransactions: data.week_comparison?.this_week_transactions || data.this_week?.transaction_count || 0,
        lastWeekTransactions: data.week_comparison?.last_week_transactions || 0
      });
      
      // Update top products from monthly_sales
      // If monthly aggregation hasn't been computed yet (e.g. current month
      // in progress), the list will be empty — fall back to live last-7-days.
      const liveTop = data.top_products || [];
      if (liveTop.length === 0) {
        await loadTopProducts();
      } else {
        setTopProducts(liveTop);
      }
      
      // Update sales by day
      setSalesByDay(data.sales_by_day || []);
      
      // Store full summary for display
      setMonthSummary(data.this_month);
      
    } catch (error) {
      console.error("Error loading dashboard summary:", error);
      // Fallback to original method
      await Promise.all([
        loadTodaySales(),
        loadTopProducts(),
        loadSalesByDay(),
        loadWeekComparison(),
      ]);
    }
  };

  const loadTodaySales = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/api/sales/transactions?start_date=${today}&end_date=${today}T23:59:59`);
      const sales = res.data || [];
      
      const total = sales.reduce((sum, s) => sum + (s.total || 0), 0);
      const count = sales.length;
      
      // Get target from projected income (last year same day average)
      let target = 0;
      try {
        const projRes = await axios.get(`${API}/api/budgets/projected-income`);
        target = projRes.data?.projections?.daily?.turnover || 0;
      } catch (e) {
        // Use default target if no projection available
        target = 10000;
      }
      
      setTodaySales({ total, count, target });
    } catch (error) {
      console.error("Error loading today's sales:", error);
    }
  };

  const loadTopProducts = async () => {
    try {
      // Get sales from last 7 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      
      const res = await axios.get(`${API}/api/sales/transactions?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString()}`);
      const sales = res.data || [];
      
      // Aggregate products
      const productMap = new Map();
      sales.forEach(sale => {
        (sale.items || []).forEach(item => {
          const key = item.product_id || item.plu_code;
          const existing = productMap.get(key) || {
            id: key,
            name: item.description || item.name || 'Unknown',
            plu_code: item.plu_code,
            quantity: 0,
            revenue: 0
          };
          existing.quantity += item.quantity || 0;
          existing.revenue += (item.total || (item.quantity * item.unit_price)) || 0;
          productMap.set(key, existing);
        });
      });
      
      const sorted = Array.from(productMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);
      
      setTopProducts(sorted);
    } catch (error) {
      console.error("Error loading top products:", error);
    }
  };

  const loadLowStock = async () => {
    try {
      const res = await axios.get(`${API}/api/products/low-stock?limit=10`);
      setLowStockItems(res.data || []);
    } catch (error) {
      // Try alternative endpoint
      try {
        const res = await axios.get(`${API}/api/products?low_stock=true&limit=10`);
        const lowStock = (res.data || []).filter(p => 
          (p.current_stock || 0) <= (p.reorder_level || p.minimum_level || 5)
        ).slice(0, 10);
        setLowStockItems(lowStock);
      } catch (e) {
        console.error("Error loading low stock:", e);
      }
    }
  };

  const loadSalesByDay = async () => {
    try {
      // Get sales from last 4 weeks to calculate average by day
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 28);
      
      const res = await axios.get(`${API}/api/sales/transactions?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString()}`);
      const sales = res.data || [];
      
      // Aggregate by day of week
      const dayTotals = [0, 0, 0, 0, 0, 0, 0];
      const dayCounts = [0, 0, 0, 0, 0, 0, 0];
      
      sales.forEach(sale => {
        const date = new Date(sale.created_at);
        const dayOfWeek = date.getDay();
        dayTotals[dayOfWeek] += sale.total || 0;
        dayCounts[dayOfWeek]++;
      });
      
      const avgByDay = WEEKDAYS.map((day, idx) => ({
        day,
        dayShort: WEEKDAYS_SHORT[idx],
        average: dayCounts[idx] > 0 ? dayTotals[idx] / (28 / 7) : 0,
        total: dayTotals[idx],
        transactions: dayCounts[idx]
      }));
      
      setSalesByDay(avgByDay);
    } catch (error) {
      console.error("Error loading sales by day:", error);
    }
  };

  const loadSalesByHour = async () => {
    try {
      // Get sales from last 7 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      
      const res = await axios.get(`${API}/api/sales/transactions?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString()}`);
      const sales = res.data || [];
      
      // Aggregate by hour (business hours 6am - 8pm)
      const hourTotals = {};
      for (let h = 6; h <= 20; h++) {
        hourTotals[h] = { hour: h, total: 0, count: 0 };
      }
      
      sales.forEach(sale => {
        const date = new Date(sale.created_at);
        const hour = date.getHours();
        if (hourTotals[hour]) {
          hourTotals[hour].total += sale.total || 0;
          hourTotals[hour].count++;
        }
      });
      
      const hourData = Object.values(hourTotals).map(h => ({
        ...h,
        label: `${h.hour}:00`,
        average: h.count > 0 ? h.total / 7 : 0
      }));
      
      setSalesByHour(hourData);
    } catch (error) {
      console.error("Error loading sales by hour:", error);
    }
  };

  const loadWeekComparison = async () => {
    try {
      const today = new Date();
      
      // This week (Mon-today)
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - today.getDay() + 1);
      
      // Last week
      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(lastWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(thisWeekStart);
      lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
      
      const [thisWeekRes, lastWeekRes] = await Promise.all([
        axios.get(`${API}/api/sales/transactions?start_date=${thisWeekStart.toISOString().split('T')[0]}&end_date=${today.toISOString()}`),
        axios.get(`${API}/api/sales/transactions?start_date=${lastWeekStart.toISOString().split('T')[0]}&end_date=${lastWeekEnd.toISOString()}`)
      ]);
      
      const thisWeekTotal = (thisWeekRes.data || []).reduce((sum, s) => sum + (s.total || 0), 0);
      const lastWeekTotal = (lastWeekRes.data || []).reduce((sum, s) => sum + (s.total || 0), 0);
      const change = lastWeekTotal > 0 ? ((thisWeekTotal - lastWeekTotal) / lastWeekTotal) * 100 : 0;
      
      setWeekComparison({ thisWeek: thisWeekTotal, lastWeek: lastWeekTotal, change });
    } catch (error) {
      console.error("Error loading week comparison:", error);
    }
  };

  const loadTopCategories = async () => {
    try {
      // Get sales from last 7 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      
      const res = await axios.get(`${API}/api/sales/transactions?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString()}`);
      const sales = res.data || [];
      
      // Aggregate by category
      const categoryMap = new Map();
      sales.forEach(sale => {
        (sale.items || []).forEach(item => {
          const category = item.category || 'Uncategorized';
          const existing = categoryMap.get(category) || { name: category, revenue: 0, quantity: 0 };
          existing.revenue += (item.total || (item.quantity * item.unit_price)) || 0;
          existing.quantity += item.quantity || 0;
          categoryMap.set(category, existing);
        });
      });
      
      const sorted = Array.from(categoryMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      
      setTopCategories(sorted);
    } catch (error) {
      console.error("Error loading top categories:", error);
    }
  };

  const loadInsights = async () => {
    try {
      const res = await axios.get(`${API}/api/dashboard/insights`);
      setInsights(res.data);
    } catch (error) {
      console.error("Error loading insights:", error);
    }
  };

  const loadProductsNeedingReview = async () => {
    try {
      const res = await axios.get(`${API}/api/products/needs-review`);
      setProductsNeedingReview(res.data);
    } catch (error) {
      console.error("Error loading products needing review:", error);
    }
  };

  const clearReviewFlag = async (productId, productName) => {
    try {
      await axios.post(`${API}/api/products/${productId}/clear-review-flag`);
      toast.success(`Review cleared for ${productName}`);
      loadProductsNeedingReview();
    } catch (error) {
      toast.error("Failed to clear review flag");
    }
  };

  const bestDay = salesByDay.reduce((best, day) => day.average > best.average ? day : best, { average: 0 });
  const bestHour = salesByHour.reduce((best, hour) => hour.average > best.average ? hour : best, { average: 0 });
  const maxDayAvg = Math.max(...salesByDay.map(d => d.average), 1);
  const maxHourAvg = Math.max(...salesByHour.map(h => h.average), 1);
  const salesProgress = todaySales.target > 0 ? (todaySales.total / todaySales.target) * 100 : 0;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen" data-testid="smart-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Smart Dashboard</h1>
          <p className="text-gray-500">Real-time business insights</p>
        </div>
        <Button onClick={refreshData} disabled={refreshing} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <ImportanceOneAlert />

      {/* Today's Performance */}
      <SalesPatternCard />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Today's Sales */}
        <Card className="col-span-2 bg-gradient-to-br from-purple-500 to-purple-700 text-white">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-purple-100 text-sm font-medium">Today's Sales</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(todaySales.total)}</p>
                <p className="text-purple-200 text-sm mt-1">{todaySales.count} transactions</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <DollarSign className="h-6 w-6" />
              </div>
            </div>
            
            {/* Progress to target */}
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-purple-200">Progress to daily target</span>
                <span className="font-medium">{salesProgress.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(salesProgress, 100)}%` }}
                />
              </div>
              <p className="text-purple-200 text-xs mt-1">
                Target: {formatCurrency(todaySales.target)}
                {salesProgress >= 100 && <span className="ml-2">🎉 Target achieved!</span>}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Week Comparison */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm font-medium">This Week</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(weekComparison.thisWeek)}</p>
                <p className="text-gray-400 text-xs">{weekComparison.thisWeekTransactions || 0} transactions</p>
              </div>
              <div className={`p-2 rounded-lg ${weekComparison.change >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {weekComparison.change >= 0 ? (
                  <TrendingUp className="h-5 w-5 text-green-600" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-red-600" />
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Badge variant={weekComparison.change >= 0 ? "default" : "destructive"}>
                {weekComparison.change >= 0 ? '+' : ''}{weekComparison.change.toFixed(1)}%
              </Badge>
              <span className="text-sm text-gray-500">vs last week</span>
            </div>
            {/* Show this month total */}
            {monthSummary && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-gray-500 text-xs font-medium">This Month</p>
                <p className="text-lg font-bold text-emerald-600">{formatCurrency(monthSummary.total)}</p>
                <p className="text-xs text-gray-400">
                  {monthSummary.transaction_count > 0 ? `${monthSummary.transaction_count} live transactions` : ''}
                  {monthSummary.source === 'imported' ? ' (From imported data)' : ''}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alert */}
        <Card className={lowStockItems.length > 5 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm font-medium">Low Stock Items</p>
                <p className="text-2xl font-bold mt-1">{lowStockItems.length}</p>
              </div>
              <div className={`p-2 rounded-lg ${lowStockItems.length > 5 ? 'bg-red-200' : 'bg-orange-100'}`}>
                <AlertTriangle className={`h-5 w-5 ${lowStockItems.length > 5 ? 'text-red-600' : 'text-orange-600'}`} />
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {lowStockItems.length > 0 ? 'Need attention' : 'All stocked up!'}
            </p>
          </CardContent>
        </Card>

        {/* Transaction Count Summary */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm font-medium">Transactions Today</p>
                <p className="text-2xl font-bold mt-1">{todaySales.count}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            {monthSummary && (
              <div className="mt-3 pt-3 border-t space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">This Month:</span>
                  <span className="font-semibold">{monthSummary.transaction_count || monthSummary.count || '—'}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top 10 Products */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-500" />
              Top 10 Selling Products (This Week)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ShoppingCart className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No sales data available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topProducts.map((product, idx) => {
                  const maxRevenue = topProducts[0]?.revenue || 1;
                  const percentage = (product.revenue / maxRevenue) * 100;
                  
                  return (
                    <div key={product.id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-gray-200 text-gray-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-sm truncate">{product.name}</span>
                          <span className="text-sm font-bold text-green-600">{formatCurrency(product.revenue)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 w-16">{product.quantity} sold</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Low Stock Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lowStockItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>All items well stocked!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {lowStockItems.slice(0, 8).map((item) => (
                  <div 
                    key={item.id || item.plu_code}
                    className="flex items-center justify-between p-2 bg-orange-50 rounded-lg border border-orange-200"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{item.description || item.name}</p>
                      <p className="text-xs text-gray-500">PLU: {item.plu_code}</p>
                    </div>
                    <Badge variant="destructive" className="ml-2">
                      {item.current_stock || 0} left
                    </Badge>
                  </div>
                ))}
                {lowStockItems.length > 8 && (
                  <p className="text-sm text-center text-gray-500 mt-2">
                    +{lowStockItems.length - 8} more items
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Best Performing Times */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Best Days */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Best Performing Days
              {bestDay.day && (
                <Badge className="ml-auto bg-green-100 text-green-700">
                  Best: {bestDay.day}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {salesByDay.map((day) => (
                <div key={day.day} className="flex items-center gap-3">
                  <span className={`w-12 text-sm font-medium ${day.day === bestDay.day ? 'text-green-600' : 'text-gray-600'}`}>
                    {day.dayShort}
                  </span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-lg overflow-hidden relative">
                    <div 
                      className={`h-full rounded-lg transition-all ${
                        day.day === bestDay.day ? 'bg-green-500' : 'bg-blue-400'
                      }`}
                      style={{ width: `${(day.average / maxDayAvg) * 100}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                      {formatCurrency(day.average)}/day avg
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Best Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" />
              Best Performing Hours
              {bestHour.label && (
                <Badge className="ml-auto bg-purple-100 text-purple-700">
                  Peak: {bestHour.label}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-40">
              {salesByHour.map((hour) => {
                const height = maxHourAvg > 0 ? (hour.average / maxHourAvg) * 100 : 0;
                const isPeak = hour.hour === bestHour.hour;
                
                return (
                  <div key={hour.hour} className="flex-1 h-full flex flex-col items-center justify-end">
                    <div 
                      className={`w-full rounded-t transition-all ${
                        isPeak ? 'bg-purple-500' : 'bg-blue-300'
                      }`}
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${hour.label}: ${formatCurrency(hour.average)} avg`}
                    />
                    <span className="text-xs text-gray-500 mt-1 transform -rotate-45 origin-center">
                      {hour.hour}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              Peak sales hour: <span className="font-bold text-purple-600">{bestHour.label}</span>
              {bestHour.average > 0 && ` (avg ${formatCurrency(bestHour.average)})`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Daily Target</p>
                <p className="text-lg font-bold text-blue-900">{formatCurrency(todaySales.target)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-green-600 font-medium">Best Day</p>
                <p className="text-lg font-bold text-green-900">{bestDay.day || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-purple-600 font-medium">Peak Hour</p>
                <p className="text-lg font-bold text-purple-900">{bestHour.label || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-orange-600 font-medium">Top Product</p>
                <p className="text-sm font-bold text-orange-900 truncate max-w-[120px]">
                  {topProducts[0]?.name || 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Smart Insights Section */}
      {insights && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-6 w-6 text-yellow-500" />
            <h2 className="text-xl font-bold text-gray-900">Smart Insights</h2>
            <Badge variant="outline" className="ml-2">Rule-Based • Free</Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Critical Alerts */}
            {insights.alerts && insights.alerts.length > 0 && (
              <Card className="border-red-300 bg-red-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" />
                    Critical Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {insights.alerts.map((alert, idx) => (
                      <div key={idx} className="p-2 bg-white rounded border border-red-200">
                        <div className="flex items-start gap-2">
                          <span className="text-lg">{alert.icon}</span>
                          <div>
                            <p className="font-medium text-sm text-red-800">{alert.title}</p>
                            <p className="text-xs text-red-600">{alert.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sales Trends */}
            {insights.trends && insights.trends.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Sales Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {insights.trends.map((trend, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${
                        trend.type === 'trending_up' ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{trend.icon}</span>
                          <span className={`font-medium ${trend.type === 'trending_up' ? 'text-green-700' : 'text-orange-700'}`}>
                            {trend.title}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{trend.message}</p>
                        {trend.details && trend.details.slice(0, 3).map((item, i) => (
                          <div key={i} className="flex justify-between text-xs py-1 border-t border-gray-200">
                            <span className="truncate max-w-[150px]">{item.name}</span>
                            <Badge variant={trend.type === 'trending_up' ? 'default' : 'destructive'} className="text-xs">
                              {item.change_percent > 0 ? '+' : ''}{item.change_percent}%
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reorder Alerts */}
            {insights.reorder_alerts && insights.reorder_alerts.details && (
              <Card className={insights.reorder_alerts.priority === 'critical' ? 'border-red-300' : 'border-yellow-300'}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <RotateCcw className="h-5 w-5 text-yellow-600" />
                    Reorder Soon
                    {insights.reorder_alerts.critical_count > 0 && (
                      <Badge variant="destructive" className="ml-auto">{insights.reorder_alerts.critical_count} critical</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">{insights.reorder_alerts.message}</p>
                  <div className="space-y-2">
                    {insights.reorder_alerts.details.slice(0, 5).map((item, idx) => (
                      <div key={idx} className={`p-2 rounded border ${
                        item.days_remaining < 3 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm truncate max-w-[180px]">{item.name}</p>
                            <p className="text-xs text-gray-500">Stock: {item.current_stock} • Sells {item.daily_velocity}/day</p>
                          </div>
                          <Badge variant={item.days_remaining < 3 ? 'destructive' : 'outline'}>
                            {item.days_remaining} days
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Slow Movers */}
            {insights.slow_movers && insights.slow_movers.details && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <Snail className="h-5 w-5 text-gray-500" />
                    Slow Moving Stock
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-3 p-2 bg-gray-100 rounded">
                    <p className="text-sm text-gray-600">{insights.slow_movers.message}</p>
                    <p className="text-lg font-bold text-gray-800">
                      {formatCurrency(insights.slow_movers.total_stuck_value)} stuck value
                    </p>
                  </div>
                  <div className="space-y-2">
                    {insights.slow_movers.details.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded border">
                        <div>
                          <p className="font-medium text-sm truncate max-w-[150px]">{item.name}</p>
                          <p className="text-xs text-gray-500">Stock: {item.current_stock}</p>
                        </div>
                        <span className="text-sm font-medium text-gray-600">
                          {formatCurrency(item.stock_value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Performance Insights */}
            {insights.performance && insights.performance.length > 0 && (
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-indigo-500" />
                    Performance Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {insights.performance.map((perf, idx) => (
                      <div key={idx} className="p-3 bg-white rounded-lg border border-indigo-100">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">{perf.icon}</span>
                          <span className="font-medium text-indigo-800">{perf.title}</span>
                        </div>
                        <p className="text-sm text-gray-600">{perf.message}</p>
                        {perf.value && (
                          <p className="text-lg font-bold text-indigo-600 mt-1">{perf.value}</p>
                        )}
                        {perf.suggestion && (
                          <p className="text-xs text-indigo-500 mt-1 italic">💡 {perf.suggestion}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary Stats */}
            {insights.summary && insights.summary.total_revenue > 0 && (
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-emerald-500" />
                    30-Day Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Total Revenue</p>
                      <p className="text-lg font-bold text-emerald-700">{formatCurrency(insights.summary.total_revenue)}</p>
                    </div>
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Transactions</p>
                      <p className="text-lg font-bold text-emerald-700">{insights.summary.transaction_count}</p>
                    </div>
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Avg Transaction</p>
                      <p className="text-lg font-bold text-emerald-700">{formatCurrency(insights.summary.avg_transaction)}</p>
                    </div>
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">vs Last Period</p>
                      <p className={`text-lg font-bold ${insights.summary.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {insights.summary.revenue_change_percent >= 0 ? '+' : ''}{insights.summary.revenue_change_percent}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Products Needing Stock Level Review */}
      {productsNeedingReview && productsNeedingReview.total_needing_attention > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertCircle className="h-5 w-5" />
              Products Needing Stock Level Setup
              <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800 border-amber-300">
                {productsNeedingReview.total_needing_attention} items
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-700 mb-4">
              These products need attention to ensure you don't run out unexpectedly.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Flagged after first sale */}
              {productsNeedingReview.flagged_after_sale?.length > 0 && (
                <div className="bg-white rounded-lg border border-amber-200 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingCart className="h-4 w-4 text-amber-600" />
                    <h4 className="font-semibold text-sm text-amber-800">First Sale - Set Levels</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    These products had their first sale but no reorder levels were set. Minimum set to 1.
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {productsNeedingReview.flagged_after_sale.slice(0, 5).map(product => (
                      <div key={product.id} className="flex justify-between items-center p-2 bg-amber-50 rounded text-sm">
                        <div className="truncate flex-1 mr-2">
                          <span className="font-mono text-xs text-amber-600">{product.plu_code}</span>
                          <span className="ml-1">{product.name?.substring(0, 25)}</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-6 text-xs px-2"
                          onClick={() => clearReviewFlag(product.id, product.name)}
                        >
                          Done
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Auto-set levels from GRV */}
              {productsNeedingReview.auto_set_levels?.length > 0 && (
                <div className="bg-white rounded-lg border border-blue-200 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-4 w-4 text-blue-600" />
                    <h4 className="font-semibold text-sm text-blue-800">Auto-Set Levels (Review)</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Stock levels were auto-calculated when receiving. Verify they are correct.
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {productsNeedingReview.auto_set_levels.slice(0, 5).map(product => (
                      <div key={product.id} className="flex justify-between items-center p-2 bg-blue-50 rounded text-sm">
                        <div className="truncate flex-1 mr-2">
                          <span className="font-mono text-xs text-blue-600">{product.plu_code}</span>
                          <span className="ml-1">{product.name?.substring(0, 20)}</span>
                          <span className="text-xs text-gray-400 ml-1">
                            (Min:{product.minimum_level} Re:{product.reorder_level})
                          </span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-6 text-xs px-2"
                          onClick={() => clearReviewFlag(product.id, product.name)}
                        >
                          OK
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* At risk - no levels set */}
              {productsNeedingReview.at_risk_no_levels?.length > 0 && (
                <div className="bg-white rounded-lg border border-red-200 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <h4 className="font-semibold text-sm text-red-800">At Risk - No Levels</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Products with stock but no reorder alerts configured. They could sell out unnoticed!
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {productsNeedingReview.at_risk_no_levels.slice(0, 5).map(product => (
                      <div key={product.id} className="p-2 bg-red-50 rounded text-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-mono text-xs text-red-600">{product.plu_code}</span>
                          <span className="text-xs text-gray-500">Stock: {product.current_stock}</span>
                        </div>
                        <p className="text-sm truncate">{product.name?.substring(0, 30)}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Go to Product Master to set stock levels
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
